import { CaseStatusIndicator } from "@/components/CaseStatus/CaseStatus";
import { DesignTableEmptyState } from "@/components/EmptyState/DesignTableEmptyState";
import { DisplayError } from "@/components/Error/Error";
import { Column, DynamicTable, Sort } from "@/components/Table/Table";
import { constructSelectRangeOfRows } from "@/components/Table/tableHelpers";
import { CaseThumbnail } from "@/components/Thumbnail/CaseThumbnail";
import { COMPLETE_CASE_STATUSES } from "@/constants/common";
import { CASE_TABLE_SORT_FIELDS, CASE_TABLE_STATE_NAME } from "@/constants/designTableConstants";
import { CaseReadPublic, CaseStatus, clientApi } from "@/store/clientApi";
import {
  addSelectedId,
  removeSelectedId,
  selectDetailsItemId,
  selectDetailsItemLabel,
  selectDetailsItemType,
  setDetailsItem,
  setDrawerOpen
} from "@/store/slices/detailsDrawerSlice";
import {
  selectChecked,
  selectQueryIsSearching,
  selectRtkData,
  setCanCollapsible,
  setSortField,
  setSortOrder,
  setTableChecked,
  toggleChecked
} from "@/store/slices/tableSlice";
import { RootState, store } from "@/store/store";
import { ModelLabel, ModelType } from "@/typed/ModelTypes";
import { formatDateTime } from "@/utils/date";
import { Store } from "@/utils/indexeddb";
import { Box, TableCell, TableRow, Theme, Typography, useMediaQuery, useTheme } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { createSelector } from "@reduxjs/toolkit";
import { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

interface ScanRow {
  thumbnailType: "input-thumbnail" | "output-thumbnail";
  modelType: ModelType;
  label: "upper" | "lower";
}

const upperScan: ScanRow = {
  thumbnailType: "input-thumbnail",
  modelType: "scan",
  label: "upper"
};
const lowerScan: ScanRow = {
  thumbnailType: "input-thumbnail",
  modelType: "scan",
  label: "lower"
};
const upperDesigned: ScanRow = {
  thumbnailType: "output-thumbnail",
  modelType: "based-model",
  label: "upper"
};
const lowerDesigned: ScanRow = {
  thumbnailType: "output-thumbnail",
  modelType: "based-model",
  label: "lower"
};

const EntityCell = ({ dentalCase, column }: { dentalCase: CaseReadPublic; column: Column }) => {
  const { t } = useTranslation("cases");

  const CASE_NAME = t("title.caseName");
  const STATUS = t("title.status");
  const RECEIVED = t("title.received");
  const UNAVAILABLE = t("common:loading.unavailable");

  useEffect(() => {
    store.dispatch(setCanCollapsible({ name: CASE_TABLE_STATE_NAME, index: dentalCase.id.toString(), value: true }));
  }, [dentalCase.id]);

  const thumbnailStoreName = COMPLETE_CASE_STATUSES.includes(dentalCase.status) ? Store.OutputThumbnailStore : Store.ScanThumbnailStore;

  const formattedDate = dentalCase.created_at ? formatDateTime(dentalCase.created_at) : "-";

  return (
    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
      <Box sx={{ width: "100%" }}>
        {column.name === CASE_NAME && (
          // Show thumbnail and name together
          <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: "16px" }}>
            <CaseThumbnail id={dentalCase.id} status={dentalCase.status} label="combined" storeName={thumbnailStoreName} />
            <Typography
              noWrap
              sx={{ overflow: "hidden", textOverflow: "ellipsis", cursor: "text", userSelect: "text", width: "max-content" }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {dentalCase ? dentalCase.name : UNAVAILABLE}
            </Typography>
          </Box>
        )}
        {column.name === STATUS && dentalCase && <CaseStatusIndicator caseStatus={dentalCase.status} eventLogs={dentalCase.logs ?? []} />}
        {column.name === RECEIVED && dentalCase && (
          <Typography noWrap sx={{ cursor: "text", userSelect: "text", width: "100%" }} onMouseDown={(e) => e.stopPropagation()}>
            {formattedDate}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

interface ThumbnailRowProps {
  thumbnailType: "input-thumbnail" | "output-thumbnail";
  label: "upper" | "lower";
  caseId: string;
  selected: boolean;
  caseSelected: boolean;
  last: boolean;
  status: CaseStatus;
  onClick: (event: React.MouseEvent<HTMLTableRowElement>) => void;
}

const ThumbnailRow: FC<ThumbnailRowProps> = ({ thumbnailType, label, caseId, selected, last, status, onClick, caseSelected }) => {
  const { t } = useTranslation("cases");

  const thumbnailStoreName = thumbnailType === "input-thumbnail" ? Store.ScanThumbnailStore : Store.OutputThumbnailStore;

  return (
    <TableRow
      sx={[
        {
          backgroundColor: selected ? (theme: Theme) => theme.surface.high + " !important" : "inherit",
          cursor: "pointer",
          "&&:hover": { backgroundColor: !caseSelected ? (theme: Theme) => theme.surface.normal : undefined }
        },
        !last && { "> td": { border: "none" } }
      ]}
      selected={caseSelected}
      onClick={onClick}
    >
      <TableCell colSpan={2}></TableCell>
      <TableCell colSpan={3}>
        <Box sx={{ display: "flex", gap: "0.5em", justifyContent: "flex-start", alignItems: "center" }}>
          <CaseThumbnail size={40} id={Number(caseId)} status={status} label={label} storeName={thumbnailStoreName} />
          <Typography sx={{ cursor: "text", userSelect: "text" }} onMouseDown={(e) => e.stopPropagation()}>
            {thumbnailType === "output-thumbnail" && `${t("label.designedModel")} `}
            {t(
              /*
              t('scans.upper')
              t('scans.lower')
              */
              `scans.${label}`
            )}
            {thumbnailType === "input-thumbnail" && ` ${t("label.scan")}`}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );
};

const CollapsibleDetails = ({
  dentalCase,
  visible,
  selectRows
}: {
  dentalCase: CaseReadPublic;
  visible: boolean;
  selectRows: (id: string, itemType: ModelType) => void;
}) => {
  const { id: selectedTab } = useParams();
  const location = useLocation();
  const basePath = location.pathname.split("/")[1];

  // Retrieve selection state from the store.
  const selectedId = useSelector((state: RootState) => selectDetailsItemId(state, basePath, selectedTab));
  const selectedItemLabel = useSelector((state: RootState) => selectDetailsItemLabel(state, basePath, selectedTab));
  const selectedItemType = useSelector((state: RootState) => selectDetailsItemType(state, basePath, selectedTab));
  const checked = useSelector((state: RootState) => selectChecked(state, CASE_TABLE_STATE_NAME, dentalCase.id.toString()));

  // This click handler toggles the selection for a subrow.
  const handleClick = (event: React.MouseEvent<HTMLTableRowElement>, itemLabel: ModelLabel, itemType: ModelType) => {
    event.stopPropagation();
    const id = dentalCase.id.toString();

    if (event.ctrlKey) {
      if (checked) {
        store.dispatch(
          removeSelectedId({
            page: basePath,
            tab: selectedTab,
            selectedId: id
          })
        );
      } else {
        store.dispatch(
          addSelectedId({
            page: basePath,
            tab: selectedTab,
            selectedId: id,
            itemLabel: itemLabel,
            itemType: itemType
          })
        );
      }
      store.dispatch(toggleChecked({ name: CASE_TABLE_STATE_NAME, checked: id }));
      return;
    } else if (event.shiftKey) {
      selectRows(id, itemType);
      return;
    }

    store.dispatch(
      setDetailsItem({
        page: basePath,
        tab: selectedTab,
        selectedId: id,
        itemType: itemType,
        itemLabel: itemLabel
      })
    );
    store.dispatch(
      setDrawerOpen({
        value: true
      })
    );
    store.dispatch(
      setTableChecked({
        name: CASE_TABLE_STATE_NAME,
        checked: { [id]: true },
        lastChecked: id
      })
    );
  };

  const collapsedRows = useMemo(() => {
    const rows: ScanRow[] = [];

    const content = dentalCase.content;
    const status = dentalCase.status;

    if (content === "combined" || content === "lower") {
      rows.push(lowerScan);
      if (COMPLETE_CASE_STATUSES.includes(status)) {
        rows.push(lowerDesigned);
      }
    }

    if (content === "combined" || content === "upper") {
      rows.push(upperScan);
      if (COMPLETE_CASE_STATUSES.includes(status)) {
        rows.push(upperDesigned);
      }
    }

    return rows;
  }, [dentalCase.content, dentalCase.status]);

  if (!visible) {
    return <></>;
  }

  return (
    <>
      {collapsedRows.map((row, idx) => {
        // Determine if this subrow is selected.
        // If the selectedItemLabel and selectedItemType are defined, then a subrow is selected.
        const subrowSelected =
          selectedId === dentalCase.id.toString() && selectedItemLabel === row.label && selectedItemType === row.modelType;
        return (
          <ThumbnailRow
            {...row}
            key={`${row.thumbnailType}-${row.label}`}
            caseId={dentalCase.id.toString()}
            selected={subrowSelected}
            caseSelected={checked}
            status={dentalCase.status}
            last={idx === collapsedRows.length - 1}
            onClick={(event: React.MouseEvent<HTMLTableRowElement>) => {
              handleClick(event, row.label, row.modelType);
            }}
          />
        );
      })}
    </>
  );
};

const CasesList = () => {
  const { id: selectedTab } = useParams();
  const basePath = useLocation().pathname.split("/")[1];
  const theme = useTheme();
  const { t } = useTranslation("cases");

  const CASE_NAME = t("title.caseName");
  const STATUS = t("title.status");
  const RECEIVED = t("title.received");

  const isSearching = useSelector((state: RootState) => selectQueryIsSearching(state, CASE_TABLE_STATE_NAME));

  const selectPaginatedCases = useMemo(() => {
    return createSelector(
      [(state: RootState) => selectRtkData(state, CASE_TABLE_STATE_NAME, clientApi.endpoints.getCasesApiV1CasesGet.select)],
      (query) => {
        return {
          cases: query?.data?.content,
          totalCount: query?.data?.total_count ?? query?.data?.content?.length,
          error: query?.error,
          isLoading: query?.isLoading && (query?.isUninitialized || query?.data === undefined)
        };
      },
      {
        memoizeOptions: {
          resultEqualityCheck: shallowEqual
        }
      }
    );
  }, []);

  // Using memoized selector for rtkData, totalCount, error, isLoading
  const { cases, totalCount, error, isLoading } = useSelector(selectPaginatedCases);

  const columns: Column[] = [
    { name: CASE_NAME, sortable: true, key: CASE_TABLE_SORT_FIELDS[0], minWidth: 200 },
    { name: STATUS, sortable: true, key: CASE_TABLE_SORT_FIELDS[1], minWidth: 150 },
    { name: RECEIVED, sortable: true, key: CASE_TABLE_SORT_FIELDS[2], minWidth: 175 }
  ];

  const checkable = useMediaQuery(theme.breakpoints.up("sm"));

  const handleSortTable = (sort?: Sort) => {
    if (sort !== undefined && sort.key !== undefined) {
      store.dispatch(setSortField({ name: CASE_TABLE_STATE_NAME, value: sort.key }));
      store.dispatch(setSortOrder({ name: CASE_TABLE_STATE_NAME, value: sort.order }));
    } else {
      store.dispatch(setSortField({ name: CASE_TABLE_STATE_NAME, value: "" }));
      store.dispatch(setSortOrder({ name: CASE_TABLE_STATE_NAME, value: "asc" }));
    }
  };

  if (error && cases === undefined) {
    // Only show error if there is no data to show
    return <DisplayError error={error} />;
  }

  if (isLoading || cases === undefined) {
    return (
      <Box
        component="div"
        sx={{
          height: "100%"
        }}
      >
        <LinearProgress />
      </Box>
    );
  }

  const mapId = (dentalCase: CaseReadPublic) => dentalCase.id.toString();

  const selectRangeOfRows = constructSelectRangeOfRows({
    stateName: CASE_TABLE_STATE_NAME,
    page: basePath,
    tab: selectedTab,
    mapId: mapId,
    data: cases
  });

  return (
    <>
      <DynamicTable<CaseReadPublic>
        stateName={CASE_TABLE_STATE_NAME}
        checkable={checkable}
        columns={columns}
        tableData={cases}
        totalNumEntities={totalCount ?? 0}
        onSort={handleSortTable}
        noMatch={isSearching && totalCount === 0}
        renderEntry={(dentalCase) => columns.map((column) => <EntityCell key={dentalCase.id} column={column} dentalCase={dentalCase} />)}
        renderCollapsible={(visible, dentalCase) => (
          <CollapsibleDetails selectRows={selectRangeOfRows} visible={visible} dentalCase={dentalCase} />
        )}
        menuOptions={[]}
        sx={{ svg: { color: "primary.main" } }}
        selectable
        mapId={mapId}
      />
      {!isSearching && totalCount === 0 && <DesignTableEmptyState />}
    </>
  );
};

export { CasesList };
